import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { BasicInfo } from './BasicInfo'
import { AdvertisementSettings } from './AdvertisementSettings'
import { useAdvertisementById } from '@/hooks/useAdvertisementById'
import { AdvertisementClinics } from './AdvertisementClinics'
import { useViewMode } from '@/stores/viewMode'

type SettingType = 'basic' | 'settings' | 'clinics'

interface AdvertisementPageProps {
  advertisementId?: number
  onOpenChange: (open: boolean) => void
  onRefresh?: () => void
}

export function AdvertisementPage({
  advertisementId,
  onOpenChange,
  onRefresh,
}: AdvertisementPageProps) {
  const [activeTab, setActiveTab] = useState<SettingType>('basic')
  const { id: supplierId } = useViewMode()
  const { advertisement, isLoading } = useAdvertisementById(supplierId, advertisementId)

  const renderContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <BasicInfo
            advertisementId={advertisementId}
            onOpenChange={onOpenChange}
            onRefresh={onRefresh}
          />
        )
      case 'settings':
        return (
          <AdvertisementSettings
            advertisementId={advertisementId}
            onOpenChange={onOpenChange}
            onRefresh={onRefresh}
          />
        )
      case 'clinics':
        return (
          <AdvertisementClinics advertisementId={advertisementId} onOpenChange={onOpenChange} />
        )
      default:
        return null
    }
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b bg-card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">
              {isLoading ? '載入中...' : `${advertisement?.title || '未命名'} 廣告設定`}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">在這裡管理廣告的基本資訊與媒體內容</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className="w-48 border-r bg-muted/30">
          <nav className="p-4 space-y-2">
            <Button
              variant={activeTab === 'basic' ? 'secondary' : 'ghost'}
              className={cn('w-full justify-start')}
              onClick={() => setActiveTab('basic')}
            >
              基本資料
            </Button>
            <Button
              variant={activeTab === 'settings' ? 'secondary' : 'ghost'}
              className={cn('w-full justify-start')}
              onClick={() => setActiveTab('settings')}
            >
              廣告設定
            </Button>
            <Button
              variant={activeTab === 'clinics' ? 'secondary' : 'ghost'}
              className={cn('w-full justify-start')}
              onClick={() => setActiveTab('clinics')}
            >
              已排程的診所
            </Button>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto h-full">{renderContent()}</div>
      </div>
    </div>
  )
}
